import { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faBook,
  faBox,
  faChevronDown,
  faChevronRight,
  faCodeCommit,
  faFileLines,
  faMagnifyingGlass,
  faMinusCircle,
  faPlus,
  faRotate,
  faScroll,
  faShieldHalved,
  faTrash,
  faTriangleExclamation,
  faWandMagicSparkles,
  faWrench,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

interface ChangelogItem {
  raw: string;
  bold?: string;
  rest?: string;
}

interface ChangelogSection {
  title: string;
  items: ChangelogItem[];
}

interface ChangelogRelease {
  date: string;
  title: string;
  sections: ChangelogSection[];
  allText: string; // for search
}

// ── Inline markdown renderer (bold, code) ─────────────────────────────────────
function InlineMarkdown({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={i} className="font-semibold text-gray-900 dark:text-white">{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`'))
          return <code key={i} className="text-[11px] font-mono px-1 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-pink-600 dark:text-pink-400">{part.slice(1, -1)}</code>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

// ── Markdown parser ────────────────────────────────────────────────────────────
function parseChangelog(md: string): ChangelogRelease[] {
  const releases: ChangelogRelease[] = [];
  let current: ChangelogRelease | null = null;
  let currentSection: ChangelogSection | null = null;

  for (const raw of md.split('\n')) {
    const line = raw.trimEnd();

    // ## 2026-02-20 — Title
    const h2 = line.match(/^## (.+?) — (.+)/) ?? line.match(/^## (.+)/);
    if (h2) {
      if (current) releases.push(current);
      current = { date: h2[1].trim(), title: h2[2]?.trim() ?? '', sections: [], allText: '' };
      currentSection = null;
      continue;
    }

    // ### Section
    const h3 = line.match(/^### (.+)/);
    if (h3 && current) {
      currentSection = { title: h3[1].trim(), items: [] };
      current.sections.push(currentSection);
      continue;
    }

    // - item (with or without bold title)
    const li = line.match(/^- (.+)/);
    if (li && currentSection) {
      const itemText = li[1];
      const boldMatch = itemText.match(/^\*\*(.+?)\*\* — (.+)/) ?? itemText.match(/^\*\*(.+?)\*\*: (.+)/) ?? itemText.match(/^\*\*(.+?)\*\*(.*)/);
      if (boldMatch) {
        currentSection.items.push({ raw: itemText, bold: boldMatch[1], rest: boldMatch[2]?.trim() });
      } else {
        currentSection.items.push({ raw: itemText });
      }
      continue;
    }

    // plain text lines inside a release but not in a section (e.g. bare list items without heading)
    const plainLi = line.match(/^  - (.+)/);
    if (plainLi && currentSection) {
      currentSection.items.push({ raw: plainLi[1] });
      continue;
    }
  }
  if (current) releases.push(current);

  // Merge duplicate section types within each release (e.g. multiple ### Changed → one)
  for (const r of releases) {
    const merged: ChangelogSection[] = [];
    const seen = new Map<string, ChangelogSection>();
    for (const s of r.sections) {
      const key = s.title.toLowerCase();
      if (seen.has(key)) {
        seen.get(key)!.items.push(...s.items);
      } else {
        seen.set(key, s);
        merged.push(s);
      }
    }
    r.sections = merged;
  }

  // Build allText for search
  for (const r of releases) {
    r.allText = [r.date, r.title, ...r.sections.flatMap(s => [s.title, ...s.items.map(i => i.raw)])].join(' ').toLowerCase();
  }

  return releases;
}

// ── Section type metadata ─────────────────────────────────────────────────────
type SectionMeta = { icon: React.ReactNode; gradient: string; badge: string; border: string; filterLabel?: string };

const DEFAULT_SECTION_META: Record<string, SectionMeta> = {
  // Standard Keep-a-Changelog names
  'added':           { icon: <FontAwesomeIcon icon={faPlus} className="h-3 w-3 text-white" />,              gradient: 'from-emerald-500 to-teal-500',    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',  border: 'border-emerald-300 dark:border-emerald-500/40',  filterLabel: 'Added' },
  'fixed':           { icon: <FontAwesomeIcon icon={faWrench} className="h-3 w-3 text-white" />,            gradient: 'from-amber-500 to-orange-500',    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',          border: 'border-amber-300 dark:border-amber-500/40',      filterLabel: 'Fixed' },
  'changed':         { icon: <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-white" />,        gradient: 'from-blue-500 to-indigo-500',     badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',              border: 'border-blue-300 dark:border-blue-500/40',        filterLabel: 'Changed' },
  'deprecated':      { icon: <FontAwesomeIcon icon={faMinusCircle} className="h-3 w-3 text-white" />,       gradient: 'from-yellow-500 to-amber-500',    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',      border: 'border-yellow-300 dark:border-yellow-500/40',    filterLabel: 'Deprecated' },
  'removed':         { icon: <FontAwesomeIcon icon={faTrash} className="h-3 w-3 text-white" />,             gradient: 'from-red-500 to-rose-500',        badge: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',                  border: 'border-red-300 dark:border-red-500/40',          filterLabel: 'Removed' },
  'security':        { icon: <FontAwesomeIcon icon={faShieldHalved} className="h-3 w-3 text-white" />,      gradient: 'from-red-500 to-rose-500',        badge: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',                  border: 'border-red-300 dark:border-red-500/40',          filterLabel: 'Security' },
  'files changed':   { icon: <FontAwesomeIcon icon={faFileLines} className="h-3 w-3 text-white" />,         gradient: 'from-slate-500 to-gray-500',      badge: 'bg-gray-100 text-gray-600 dark:bg-slate-500/20 dark:text-slate-300',            border: 'border-gray-300 dark:border-slate-500/40',       filterLabel: 'Files Changed' },
  // Legacy / alternate names
  'new features':    { icon: <FontAwesomeIcon icon={faBolt} className="h-3 w-3 text-white" />,              gradient: 'from-emerald-500 to-teal-500',    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',  border: 'border-emerald-300 dark:border-emerald-500/40',  filterLabel: 'Added' },
  'features':        { icon: <FontAwesomeIcon icon={faBolt} className="h-3 w-3 text-white" />,              gradient: 'from-emerald-500 to-teal-500',    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',  border: 'border-emerald-300 dark:border-emerald-500/40',  filterLabel: 'Added' },
  'bug fixes':       { icon: <FontAwesomeIcon icon={faWrench} className="h-3 w-3 text-white" />,            gradient: 'from-amber-500 to-orange-500',    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',          border: 'border-amber-300 dark:border-amber-500/40',      filterLabel: 'Fixed' },
  'refactors':       { icon: <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-white" />,        gradient: 'from-blue-500 to-indigo-500',     badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',              border: 'border-blue-300 dark:border-blue-500/40',        filterLabel: 'Changed' },
  'core features':   { icon: <FontAwesomeIcon icon={faBox} className="h-3 w-3 text-white" />,              gradient: 'from-violet-500 to-purple-500',   badge: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',      border: 'border-violet-300 dark:border-violet-500/40',    filterLabel: 'Added' },
  'ui redesign':     { icon: <FontAwesomeIcon icon={faWandMagicSparkles} className="h-3 w-3 text-white" />, gradient: 'from-pink-500 to-fuchsia-500',    badge: 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300',              border: 'border-pink-300 dark:border-pink-500/40',        filterLabel: 'Changed' },
  'layout fixes':    { icon: <FontAwesomeIcon icon={faWrench} className="h-3 w-3 text-white" />,            gradient: 'from-amber-500 to-orange-500',    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',          border: 'border-amber-300 dark:border-amber-500/40',      filterLabel: 'Fixed' },
  'default':         { icon: <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-white" />,        gradient: 'from-slate-500 to-gray-500',      badge: 'bg-gray-100 text-gray-600 dark:bg-slate-500/20 dark:text-slate-300',            border: 'border-gray-300 dark:border-slate-500/40' },
};

// ── Legend parser ─────────────────────────────────────────────────────────────
// Reads a YAML-style block from a <!-- changelog-legend ... --> comment.
// Format:
//   section name:
//     icon: faIconName
//     gradient: from-X to-Y
//     badge: <tailwind classes>
//     border: <tailwind classes>
//     filterLabel: Label Text
const FA_ICON_MAP: Record<string, any> = {
  faBolt, faBook, faBox, faCodeCommit, faFileLines, faMinusCircle,
  faPlus, faRotate, faScroll, faShieldHalved, faTrash, faTriangleExclamation, faWandMagicSparkles, faWrench,
};

// Known YAML props — anything else is treated as prose and skipped
const YAML_PROPS = new Set(['icon', 'gradient', 'badge', 'border', 'filterlabel']);

function parseLegend(md: string): Record<string, Partial<SectionMeta>> {
  const match = md.match(/<!--([\s\S]*?changelog-legend[\s\S]*?)-->/);
  if (!match) return {};
  const overrides: Record<string, Partial<SectionMeta>> = {};
  let currentKey: string | null = null;
  let currentEntry: Partial<SectionMeta> = {};

  for (const line of match[1].split('\n')) {
    // Indented line → property of the current entry
    if (line.match(/^\s{2,}/) && currentKey) {
      const trimmed = line.trim();
      const colonIdx = trimmed.indexOf(':');
      if (colonIdx === -1) continue;
      const prop = trimmed.slice(0, colonIdx).trim().toLowerCase();
      const val  = trimmed.slice(colonIdx + 1).trim();
      if (!YAML_PROPS.has(prop) || !val) continue;
      if (prop === 'icon') {
        if (FA_ICON_MAP[val]) currentEntry.icon = <FontAwesomeIcon icon={FA_ICON_MAP[val]} className="h-3 w-3 text-white" />;
      } else if (prop === 'gradient')    { currentEntry.gradient    = val; }
      else if (prop === 'badge')         { currentEntry.badge       = val; }
      else if (prop === 'border')        { currentEntry.border      = val; }
      else if (prop === 'filterlabel')   { currentEntry.filterLabel = val; }
    } else {
      // Non-indented line → flush previous entry, start a new one if it looks like a key
      if (currentKey && Object.keys(currentEntry).length) {
        overrides[currentKey] = currentEntry;
      }
      currentKey = null;
      currentEntry = {};
      const trimmed = line.trim();
      if (!trimmed || trimmed === 'changelog-legend') continue;
      // A section key ends with ':' and has no value on the same line
      const colonIdx = trimmed.indexOf(':');
      if (colonIdx === -1) continue;
      const key = trimmed.slice(0, colonIdx).trim().toLowerCase();
      const rest = trimmed.slice(colonIdx + 1).trim();
      // Only treat as a section key if nothing follows the colon (pure YAML mapping key)
      if (rest === '') currentKey = key;
    }
  }
  // Flush last entry
  if (currentKey && Object.keys(currentEntry).length) {
    overrides[currentKey] = currentEntry;
  }
  return overrides;
}

// Static lookup — dynamic class construction is stripped by Tailwind's purge step
const COLOUR_BADGE: Record<string, { badge: string; border: string }> = {
  emerald: { badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-500/40' },
  teal:    { badge: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',             border: 'border-teal-300 dark:border-teal-500/40' },
  amber:   { badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',         border: 'border-amber-300 dark:border-amber-500/40' },
  orange:  { badge: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',     border: 'border-orange-300 dark:border-orange-500/40' },
  blue:    { badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',             border: 'border-blue-300 dark:border-blue-500/40' },
  indigo:  { badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',     border: 'border-indigo-300 dark:border-indigo-500/40' },
  red:     { badge: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',                 border: 'border-red-300 dark:border-red-500/40' },
  rose:    { badge: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',             border: 'border-rose-300 dark:border-rose-500/40' },
  yellow:  { badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',     border: 'border-yellow-300 dark:border-yellow-500/40' },
  violet:  { badge: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',     border: 'border-violet-300 dark:border-violet-500/40' },
  purple:  { badge: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',     border: 'border-purple-300 dark:border-purple-500/40' },
  pink:    { badge: 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300',             border: 'border-pink-300 dark:border-pink-500/40' },
  slate:   { badge: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',         border: 'border-slate-300 dark:border-slate-500/40' },
  gray:    { badge: 'bg-gray-100 text-gray-600 dark:bg-slate-500/20 dark:text-slate-300',           border: 'border-gray-300 dark:border-slate-500/40' },
};

function deriveBadgeAndBorder(gradient: string): { badge: string; border: string } {
  const m = gradient.match(/from-([a-z]+)-\d+/);
  const colour = m ? m[1] : null;
  return COLOUR_BADGE[colour ?? ''] ?? { badge: DEFAULT_SECTION_META.default.badge, border: DEFAULT_SECTION_META.default.border };
}

function buildSectionMeta(legendOverrides: Record<string, Partial<SectionMeta>>): Record<string, SectionMeta> {
  const merged = { ...DEFAULT_SECTION_META };
  for (const [key, override] of Object.entries(legendOverrides)) {
    const base = merged[key] ?? DEFAULT_SECTION_META.default;
    const withOverride = { ...base, ...override };
    // Auto-derive badge/border from gradient if not explicitly provided
    if (override.gradient && !override.badge) {
      const derived = deriveBadgeAndBorder(override.gradient);
      withOverride.badge = derived.badge;
      withOverride.border = derived.border;
    }
    merged[key] = withOverride;
  }
  return merged;
}

function getSectionMeta(title: string, sectionMeta: Record<string, SectionMeta>) {
  const key = title.toLowerCase();
  for (const [k, v] of Object.entries(sectionMeta)) {
    if (key === k || key.includes(k)) return v;
  }
  return sectionMeta.default;
}

// ── Date utilities ─────────────────────────────────────────────────────────────
function formatReleaseDate(dateStr: string): { display: string; isRecent: boolean } {
  const m = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { display: dateStr, isRecent: false };
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const diffDays = (Date.now() - d.getTime()) / 86400000;
  return {
    display: d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
    isRecent: diffDays < 30,
  };
}

// ── Main Component ─────────────────────────────────────────────────────────────
interface ChangelogViewProps {
  inline?: boolean;
  onClose: () => void;
  /** When provided, renders this content instead of fetching /CHANGELOG.md */
  content?: string;
}

export function ChangelogView({ inline, onClose: _onClose, content: externalContent }: ChangelogViewProps) {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  // Guard: only initialise collapse state once per content source, not on every streaming re-render
  const collapseInitialisedRef = useRef(false);

  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    collapseInitialisedRef.current = false; // reset when content source changes
    if (externalContent !== undefined) {
      setMarkdown(externalContent);
      setLoading(false);
      return;
    }
    fetch('/CHANGELOG.md')
      .then(r => r.text())
      .then(text => { setMarkdown(text); setLoading(false); })
      .catch(() => setLoading(false));
  }, [externalContent]);

  const releases = useMemo(() => parseChangelog(markdown), [markdown]);

  // Once releases load, collapse all except the first — guard prevents re-firing during streaming
  useEffect(() => {
    if (releases.length === 0 || collapseInitialisedRef.current) return;
    collapseInitialisedRef.current = true;
    setCollapsed(new Set(releases.map((_, i) => i).filter(i => i !== 0)));
    const sectionKeys = new Set<string>();
    releases.forEach((r, ri) => r.sections.forEach((_, si) => {
      if (ri === 0 && si === 0) return; // auto-expand first section of latest release
      sectionKeys.add(`${ri}-${si}`);
    }));
    setCollapsedSections(sectionKeys);
  }, [releases.length]);

  const legendOverrides = useMemo(() => parseLegend(markdown), [markdown]);
  const sectionMeta = useMemo(() => buildSectionMeta(legendOverrides), [legendOverrides]);

  // Build filter tabs dynamically from all section names seen in the changelog
  const filterTabs = useMemo(() => {
    const seen = new Map<string, { label: string; badge: string; gradient: string }>();
    seen.set('all', { label: 'All', badge: '', gradient: '' });
    for (const release of releases) {
      for (const section of release.sections) {
        const meta = getSectionMeta(section.title, sectionMeta);
        if (meta.filterLabel) {
          const key = meta.filterLabel.toLowerCase();
          if (!seen.has(key)) seen.set(key, { label: meta.filterLabel, badge: meta.badge, gradient: meta.gradient });
        }
      }
    }
    return Array.from(seen.entries()).map(([id, v]) => ({ id, ...v }));
  }, [releases, sectionMeta]);

  const filtered = useMemo(() => {
    let list = releases;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r => r.allText.includes(q));
    }
    if (filterType !== 'all') {
      list = list.filter(r =>
        r.sections.some(s => {
          const meta = getSectionMeta(s.title, sectionMeta);
          return meta.filterLabel?.toLowerCase() === filterType || s.title.toLowerCase().includes(filterType);
        })
      );
    }
    return list;
  }, [releases, search, filterType, sectionMeta]);

  const toggleCollapse = (idx: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div
      className={`${inline ? 'h-[calc(100vh-64px)]' : 'fixed inset-0 z-[100]'} flex flex-col overflow-hidden font-sans`}
      style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
    >
      {/* ── Header ── */}
      <div className={`flex-shrink-0 px-6 py-4 border-b ${isDark ? 'bg-slate-900/80 border-slate-700/50' : 'bg-white/80 border-gray-200'} backdrop-blur-xl`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
              <FontAwesomeIcon icon={faScroll} className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Changelog</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {releases.length} releases · VeriGit
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3 flex-1 max-w-sm">
            <div className="relative flex-1">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-slate-500 pointer-events-none" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search changes…"
                className={`w-full pl-9 pr-8 py-2 text-sm rounded-xl border transition-colors ${isDark
                  ? 'bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-500 focus:border-indigo-500/70'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-indigo-400'
                } outline-none`}
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 mt-3">
          {filterTabs.map(tab => {
            const isActive = filterType === tab.id;
            const isAll = tab.id === 'all';
            return (
            <button
              key={tab.id}
              onClick={() => setFilterType(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                isActive
                  ? isAll
                    ? 'bg-indigo-500 text-white border-indigo-500 shadow-sm'
                    : `${tab.badge} ${tab.badge.includes('border') ? '' : 'border-transparent'} shadow-sm`
                  : isDark
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700/60 border-transparent'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 border-transparent'
              }`}
            >
              {tab.label}
            </button>
            );
          })}
          {releases.length > 0 && (
            <span className="ml-auto text-xs text-gray-400 dark:text-slate-500">
              {filtered.length} of {releases.length} releases
            </span>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
              <FontAwesomeIcon icon={faRotate} className="h-4 w-4 animate-spin" /> Loading changelog…
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <FontAwesomeIcon icon={faBook} className="h-8 w-8 text-gray-300 dark:text-slate-600" />
            <p className="text-sm text-gray-500 dark:text-slate-400">No releases match your search</p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-6 py-6 space-y-4">
            {filtered.map((release, idx) => {
              const isCollapsed = collapsed.has(idx);
              const { display, isRecent } = formatReleaseDate(release.date);
              const isFirst = idx === 0;

              return (
                <div
                  key={`${release.date}-${idx}`}
                  className={`rounded-2xl border overflow-hidden transition-all ${
                    isDark
                      ? 'bg-white/[0.03] border-white/[0.07] hover:border-white/[0.12]'
                      : 'bg-white border-gray-200/80 shadow-sm hover:shadow-md hover:border-gray-300/80'
                  }`}
                >
                  {/* Release header */}
                  <button
                    onClick={() => toggleCollapse(idx)}
                    className={`w-full flex items-start gap-4 px-5 py-4 text-left transition-colors ${
                      isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50/80'
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 mt-0.5 flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full border-2 ${
                        isFirst
                          ? 'bg-indigo-500 border-indigo-400 shadow-[0_0_8px_rgba(99,102,241,0.6)]'
                          : isRecent
                            ? 'bg-emerald-500 border-emerald-400'
                            : isDark ? 'bg-slate-600 border-slate-500' : 'bg-gray-300 border-gray-200'
                      }`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Date badge */}
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          isFirst
                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300'
                            : isRecent
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
                              : isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {display}
                        </span>
                        {isFirst && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                            LATEST
                          </span>
                        )}
                        {/* Section type pills */}
                        <div className="flex items-center gap-1">
                          {release.sections.slice(0, 3).map((s, si) => {
                            const m = getSectionMeta(s.title, sectionMeta);
                            return (
                              <span key={si} className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${m.badge} ${m.border}`}>
                                {s.title}
                              </span>
                            );
                          })}
                          {release.sections.length > 3 && (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
                              +{release.sections.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                      <h3 className={`mt-1.5 text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {release.title || release.date}
                      </h3>
                    </div>

                    {/* Item count + collapse chevron */}
                    <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                      <span className="text-xs text-gray-400 dark:text-slate-500 tabular-nums">
                        {release.sections.reduce((n, s) => n + s.items.length, 0)} changes
                      </span>
                      {isCollapsed
                        ? <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                        : <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                      }
                    </div>
                  </button>

                  {/* Sections */}
                  {!isCollapsed && (
                    <div className={`border-t ${isDark ? 'border-white/[0.06]' : 'border-gray-100'} divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-gray-50'}`}>
                      {release.sections.map((section, si) => {
                        const meta = getSectionMeta(section.title, sectionMeta);
                        const sectionKey = `${idx}-${si}`;
                        const isSectionCollapsed = collapsedSections.has(sectionKey);
                        return (
                          <div key={si}>
                            {/* Section title — clickable to collapse */}
                            <button
                              onClick={() => toggleSection(sectionKey)}
                              className={`w-full flex items-center gap-2 px-5 py-3 transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50/60'}`}
                            >
                              <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${meta.gradient} opacity-90 flex-shrink-0 flex items-center justify-center`}>
                                {meta.icon}
                              </div>
                              <h4 className={`text-xs font-semibold uppercase tracking-wide flex-1 text-left ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                {section.title}
                              </h4>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.badge} flex-shrink-0`}>
                                {section.items.length}
                              </span>
                              <FontAwesomeIcon
                                icon={isSectionCollapsed ? faChevronRight : faChevronDown}
                                className={`h-3 w-3 flex-shrink-0 ml-1 transition-transform ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                              />
                            </button>

                            {/* Items */}
                            {!isSectionCollapsed && (
                              <ul className="space-y-1.5 px-5 pb-4">
                                {section.items.map((item, ii) => (
                                  <li key={ii} className="flex items-start gap-2">
                                    <span className={`mt-[5px] flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-br ${meta.gradient} opacity-70`} />
                                    <span className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                                      {item.bold ? (
                                        <>
                                          <strong className="font-semibold text-gray-900 dark:text-white">{item.bold}</strong>
                                          {item.rest ? (
                                            <> — <InlineMarkdown text={item.rest} /></>
                                          ) : null}
                                        </>
                                      ) : (
                                        <InlineMarkdown text={item.raw} />
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Footer spacer */}
            <div className="h-6" />
          </div>
        )}
      </div>
    </div>
  );
}
